/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { Theme, ThemeOptions } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';

import { createTheme } from './createTheme';
import { RadioButton } from './RadioButton';
import type { CopyState } from '../components/HighlightCode/types';

export function createCustomizedTheme(): Theme {
	const theme = createTheme();
	const customizedComponents: ThemeOptions = {
		components: {
			MuiRadio: {
				defaultProps: {
					size: 'small',
					icon: <RadioButton/>,
					checkedIcon: <RadioButton checked={ true }/>
				},
				styleOverrides: {
					root: () => ( {
						padding: 0
					} )
				}
			},
			MuiCheckbox: {
				styleOverrides: {
					root: () => ( {
						padding: 0,
						'&.Mui-checked, &.MuiCheckbox-indeterminate': {
							'& > div': {
								borderWidth: 2
							}
						},
						'&.MuiCheckbox-sizeSmall': {
							'& > div': {
								position: 'static',
								width: 18,
								height: 18,
								borderRadius: 2,
								boxSizing: 'border-box',
								'& > span': {
									position: 'absolute',
									top: 0,
									left: 0,
									fontSize: '18px'
								}
							}
						}
					} )
				}
			},
			MuiStepIcon: {
				styleOverrides: {
					root: () => ( {
						height: '24px',
						width: '24px'
					} )
				}
			},
			MuiStepLabel: {
				styleOverrides: {
					root: ( { theme } ) => ( {
						'& .MuiStepLabel-label': {
							marginLeft: '8px'
						},
						'& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
							margin: 0
						},
						'& > .MuiStepLabel-iconContainer': {
							border: '2px solid transparent',
							padding: 0,
							borderRadius: '50%',
							transform: 'scale(1)',
							'&.Mui-active': {
								outline: '2px solid',
								outlineColor: theme.palette.primary.main,
								outlineOffset: '1px'
							}
						}
					} )
				}
			},
			MuiStepConnector: {
				styleOverrides: {
					root: ( { theme } ) => ( {
						'&.Mui-active > .MuiStepConnector-line, &.Mui-completed > .MuiStepConnector-line': {
							borderColor: theme.palette.primary.main
						}
					} )
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: ( { theme, ownerState } ) => {
						const copyState = ownerState[ 'data-copy-state' ] as CopyState | undefined;
						const color = copyState === 'failed' ? theme.palette.error[ 400 ] : '#607992';

						return {
							backgroundColor: color,
							'&.MuiTooltip-tooltipArrow > .MuiTooltip-arrow': {
								color
							}
						};
					}
				}
			},
			MuiSwitch: {
				styleOverrides: {
					sizeSmall: ( { theme } ) => ( {
						width: theme.spacing( 3.5 ),
						height: theme.spacing( 2 ),
						'& .MuiSwitch-switchBase': {
							padding: theme.spacing( 0.25 ),
							'&.Mui-checked': {
								transform: `translateX(${ theme.spacing( 1.5 ) })`
							}
						},
						'& .MuiSwitch-thumb': {
							width: theme.spacing( 1.5 ),
							height: theme.spacing( 1.5 )
						},
						'& .MuiSwitch-track': {
							borderRadius: theme.spacing( 1.5 )
						}
					} )
				}
			},
			MuiInputLabel: {
				defaultProps: {
					shrink: true
				},
				styleOverrides: {
					root: () => ( {
						position: 'absolute',
						transform: 'translate(12px, -8px) scale(0.7)',
						background: 'white',
						padding: '0 3px',
						fontWeight: 400
					} )
				}
			}
		}
	};

	return deepmerge( theme, customizedComponents );
}
